// @flow

import * as React from 'react';
import {BodyMono, BodyAltMono} from 'sail/Text';

import {useAccount} from 'docs/data';
import {useStripeClui} from '../helpers/react/provider';

export const TerminalPrompt = () => {
  const {account} = useAccount();
  const isLoggedIn = account?.merchant?.isLoggedIn ?? false;
  const merchantName = account?.merchant?.nickname ?? '';

  let prompt = '$';
  if (isLoggedIn && merchantName.length) {
    prompt = `(${merchantName}) $`;
  }

  return <BodyAltMono color="white">{prompt}</BodyAltMono>;
};

const StripeCluiTerminalInput = ({paneIdx}: {paneIdx: number}): React.Node => {
  const {
    state: {isFocused, panes, focusedPane},
  } = useStripeClui();

  const {command, cursorIndex, allowInput} = panes[paneIdx];
  const isInputFocused = isFocused && focusedPane === paneIdx;

  const [isCursorVisible, setCursorVisible] = React.useState(true);
  const intervalRef = React.useRef();

  React.useEffect(() => {
    // We reset the interval anytime the command or cursorIndex changes
    // because we don't want the cursor to blink when the user is using
    // the keyboard.
    if (intervalRef && intervalRef.current) {
      clearInterval(intervalRef.current);
      setCursorVisible(true);
    }

    // We only want to set the interval when the input is focused.
    if (isInputFocused) {
      intervalRef.current = setInterval(() => {
        setCursorVisible((currentState) => !currentState);
      }, 800);
    }

    return () => {
      if (intervalRef && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [setCursorVisible, command, cursorIndex, isInputFocused]);

  let cursorStyles = {};
  if (!isCursorVisible && isInputFocused) {
    cursorStyles = {
      backgroundColor: 'transparent',
      border: 'none',
      color: 'white',
    };
  } else if (isInputFocused) {
    cursorStyles = {
      backgroundColor: 'white',
      border: 'none',
      color: '#2a2f45',
    };
  } else {
    cursorStyles = {
      backgroundColor: 'transparent',
      border: '1px solid white',
      color: 'white',
    };
  }

  return (
    <pre style={{opacity: allowInput ? 1 : 0}}>
      <code>
        <BodyMono
          color="white"
          wordBreak="word"
          style={{display: 'inline', whiteSpace: 'pre-wrap'}}
        >
          {<TerminalPrompt />} {command.slice(0, cursorIndex)}
          <pre
            style={{
              ...cursorStyles,
              display: 'inline',
              whiteSpace: 'pre-wrap',
            }}
          >
            {command.slice(cursorIndex, cursorIndex + 1) || ' '}
          </pre>
          {command.slice(cursorIndex + 1)}
        </BodyMono>
      </code>
    </pre>
  );
};

export default StripeCluiTerminalInput;
